import { api } from './authService';

// ============================================================================
// Type Definitions
// ============================================================================

export interface PipelineProgram {
  id: number;
  name: string;
  display_name: string;
  description: string;
  setup_fee: string;
  monthly_fee: string;
  leads_per_week: number;
  features: Record<string, boolean> | string;
  is_active: boolean;
  is_custom: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  activeSubscriptions?: number;
}

export interface ProgramSubscription {
  id: number;
  client_id: string;
  program_id: number;
  subscription_status: 'active' | 'paused' | 'cancelled' | 'completed';
  start_date: string;
  end_date?: string;
  next_billing_date?: string;
  custom_setup_fee?: string;
  custom_monthly_fee: string;
  custom_leads_per_week?: number;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  payment_status?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  program_name?: string;
  program_display_name?: string;
  client_name?: string;
  client_email?: string;
}

export interface LeadDelivery {
  id: number;
  subscription_id: number;
  client_id: string;
  lead_count: number;
  delivery_date: string;
  lead_quality_score?: number;
  notes?: string;
  created_at: string;
  // Joined fields
  client_name?: string;
  program_name?: string;
}

export interface MRRAnalytics {
  totalMrr: number;
  activeSubscriptions: number;
  avgSubscriptionValue: number;
  mrrByProgram: Array<{
    programId: number;
    programName: string;
    programDisplayName: string;
    mrr: number;
    subscriptionCount: number;
  }>;
}

export interface DeliveryPerformance {
  totalLeadsDelivered: number;
  avgQualityScore: number;
  activeClientsReceivingLeads: number;
  totalDeliveries: number;
  performanceByProgram: Array<{
    programId: number;
    programName: string;
    programDisplayName: string;
    leadsDelivered: number;
    avgQuality: number;
  }>;
}

export interface RevenueAnalytics {
  totalSetupFeesCollected: number;
  projectedARR: number;
  revenueByMonth: Array<{
    period_start: string;
    period_end: string;
    mrr: number;
    new_subscriptions: number;
    churned_subscriptions: number;
  }>;
}

export interface CreateProgramRequest {
  name: string;
  displayName: string;
  description?: string;
  setupFee: number;
  monthlyFee: number;
  leadsPerWeek: number;
  features: Record<string, boolean>;
  isActive?: boolean;
  isCustom?: boolean;
  displayOrder?: number;
}

export interface CreateSubscriptionRequest {
  programId: number;
  clientId?: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  clientCompany?: string;
  startDate: string;
  customSetupFee?: number;
  customMonthlyFee?: number;
  customLeadsPerWeek?: number;
  notes?: string;
}

export interface UpdateSubscriptionRequest {
  subscriptionStatus?: 'active' | 'paused' | 'cancelled' | 'completed';
  customSetupFee?: number;
  customMonthlyFee?: number;
  customLeadsPerWeek?: number;
  notes?: string;
}

export interface RecordDeliveryRequest {
  subscriptionId: string;
  leadCount: number;
  deliveryDate: string;
  leadQualityScore?: number;
  notes?: string;
}

// ============================================================================
// API Response Types
// ============================================================================

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================================================
// Pipeline Programs API
// ============================================================================

/**
 * Fetch all active pipeline programs
 */
export async function fetchPrograms(): Promise<PipelineProgram[]> {
  const response = await api.get<ApiResponse<{ programs: PipelineProgram[] }>>(
    '/pipeline/programs'
  );
  return response.data.data.programs;
}

/**
 * Fetch a specific program by ID
 */
export async function fetchProgramById(id: number): Promise<PipelineProgram> {
  const response = await api.get<ApiResponse<{ program: PipelineProgram }>>(
    `/pipeline/programs/${id}`
  );
  return response.data.data.program;
}

/**
 * Create a new pipeline program
 */
export async function createProgram(data: CreateProgramRequest): Promise<PipelineProgram> {
  const response = await api.post<ApiResponse<{ program: PipelineProgram }>>(
    '/pipeline/programs',
    data
  );
  return response.data.data.program;
}

/**
 * Update an existing program
 */
export async function updateProgram(
  id: number,
  updates: Partial<CreateProgramRequest>
): Promise<PipelineProgram> {
  const response = await api.patch<ApiResponse<{ program: PipelineProgram }>>(
    `/pipeline/programs/${id}`,
    updates
  );
  return response.data.data.program;
}

/**
 * Delete a program
 */
export async function deleteProgram(id: number): Promise<void> {
  await api.delete(`/pipeline/programs/${id}`);
}

// ============================================================================
// Subscriptions API
// ============================================================================

/**
 * Fetch all subscriptions with optional filters
 */
export async function fetchSubscriptions(filters?: {
  status?: string;
  programId?: number;
}): Promise<ProgramSubscription[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.programId) params.append('programId', filters.programId.toString());

  const response = await api.get<ApiResponse<{ subscriptions: ProgramSubscription[] }>>(
    `/pipeline/subscriptions?${params.toString()}`
  );
  return response.data.data.subscriptions;
}

/**
 * Fetch a specific subscription by ID with detailed stats
 */
export async function fetchSubscriptionById(id: number): Promise<ProgramSubscription> {
  const response = await api.get<ApiResponse<{ subscription: ProgramSubscription }>>(
    `/pipeline/subscriptions/${id}`
  );
  return response.data.data.subscription;
}

/**
 * Create a new subscription
 */
export async function createSubscription(
  data: CreateSubscriptionRequest
): Promise<ProgramSubscription> {
  const response = await api.post<ApiResponse<{ subscription: ProgramSubscription }>>(
    '/pipeline/subscriptions',
    data
  );
  return response.data.data.subscription;
}

/**
 * Update an existing subscription
 */
export async function updateSubscription(
  id: number,
  updates: UpdateSubscriptionRequest
): Promise<ProgramSubscription> {
  const response = await api.patch<ApiResponse<{ subscription: ProgramSubscription }>>(
    `/pipeline/subscriptions/${id}`,
    updates
  );
  return response.data.data.subscription;
}

// ============================================================================
// Lead Deliveries API
// ============================================================================

/**
 * Fetch delivery history with optional filters
 */
export async function fetchDeliveries(filters?: {
  subscriptionId?: number;
  startDate?: string;
  endDate?: string;
}): Promise<LeadDelivery[]> {
  const params = new URLSearchParams();
  if (filters?.subscriptionId) params.append('subscriptionId', filters.subscriptionId.toString());
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);

  const response = await api.get<ApiResponse<{ deliveries: LeadDelivery[] }>>(
    `/pipeline/deliveries?${params.toString()}`
  );
  return response.data.data.deliveries;
}

/**
 * Record a new lead delivery
 */
export async function recordDelivery(data: RecordDeliveryRequest): Promise<LeadDelivery> {
  const response = await api.post<ApiResponse<{ delivery: LeadDelivery }>>(
    '/pipeline/deliveries',
    data
  );
  return response.data.data.delivery;
}

// ============================================================================
// Analytics API
// ============================================================================

/**
 * Fetch Monthly Recurring Revenue analytics
 */
export async function fetchMRRAnalytics(): Promise<MRRAnalytics> {
  const response = await api.get<ApiResponse<MRRAnalytics>>('/pipeline/analytics/mrr');
  return response.data.data;
}

/**
 * Fetch lead delivery performance metrics
 */
export async function fetchDeliveryPerformance(dateRange?: {
  startDate?: string;
  endDate?: string;
}): Promise<DeliveryPerformance> {
  const params = new URLSearchParams();
  if (dateRange?.startDate) params.append('startDate', dateRange.startDate);
  if (dateRange?.endDate) params.append('endDate', dateRange.endDate);

  const response = await api.get<ApiResponse<DeliveryPerformance>>(
    `/pipeline/analytics/delivery-performance?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Fetch comprehensive revenue analytics
 */
export async function fetchRevenueAnalytics(): Promise<RevenueAnalytics> {
  const response = await api.get<ApiResponse<RevenueAnalytics>>('/pipeline/analytics/revenue');
  return response.data.data;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format currency values
 */
export function formatCurrency(value: string | number): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(numValue);
}

/**
 * Format date values
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get status color for subscription status
 */
export function getStatusColor(
  status: string
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active':
      return 'success';
    case 'paused':
      return 'warning';
    case 'cancelled':
    case 'completed':
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Get quality score color
 */
export function getQualityScoreColor(score: number): 'success' | 'warning' | 'error' {
  if (score >= 90) return 'success';
  if (score >= 70) return 'warning';
  return 'error';
}

export default {
  fetchPrograms,
  fetchProgramById,
  createProgram,
  updateProgram,
  fetchSubscriptions,
  fetchSubscriptionById,
  createSubscription,
  updateSubscription,
  fetchDeliveries,
  recordDelivery,
  fetchMRRAnalytics,
  fetchDeliveryPerformance,
  fetchRevenueAnalytics,
  formatCurrency,
  formatDate,
  getStatusColor,
  getQualityScoreColor,
};
